/**
 * PR-LT3 — bucket-linetype → `DashConfig` resolution for the line-material
 * builder.
 *
 * # Why this module exists (file-size split)
 *
 * `sceneBulkLoadHelpers.ts` (the per-pass mesh builder) was already ~556
 * lines. Adding the linetype-name → dash-pattern resolution inline would push
 * it well past the 500-line module ceiling (CLAUDE.md "Контроль размера
 * файлов"). The resolution logic — building a per-name `Linetype` map from the
 * document table + core seed, then compiling each `Linetype.elements[]` into a
 * single-dash-pair `DashConfig` — is self-contained and reused per bucket, so
 * it lives here behind a small, barrel-friendly surface.
 *
 * # What it does
 *
 * The renderer's `LineMaterial` exposes only a single `(dashSize, gapSize)`
 * pair (the shader limit). `compileCustomLtype` summarises a full multi-element
 * AutoCAD pattern into that pair — the existing single-dash-pair fidelity, same
 * as the built-in `linetypeToDashConfig` registry. PR-LT3 extends that to
 * CUSTOM (document-table) linetypes by name:
 *
 *   1. Build a case-insensitive `Linetype` map of ONLY the NON-STANDARD custom
 *      entries (a name NOT present in the web standard registry
 *      `LINETYPE_DASH_REGISTRY`). FIX-3 (Codex P2): routing is by NAME, not by
 *      `source`. `importLinetypes` stamps a STANDARD name `source:"file"` (with
 *      acad.lin INCH magnitudes) whenever the imported DXF redefines it, so a
 *      `source`-based filter let those through and compiled them ~25.4× too
 *      dense vs the mm-calibrated registry. Excluding by standard-name
 *      membership routes every standard name through the mm registry regardless
 *      of source.
 *   2. For a bucket's linetype NAME:
 *        - sentinel (`Continuous` / `ByLayer` / `ByBlock` / empty) → continuous.
 *        - a STANDARD name (in `LINETYPE_DASH_REGISTRY`) → the mm-calibrated
 *          built-in `linetypeToDashConfig` registry, REGARDLESS of source.
 *        - a name present in the map (a genuine custom LTYPE, in the drawing's
 *          own units) → compile its `elements[]` via the core
 *          `signedLengthsFromLinetype` converter + `compileCustomLtype`.
 *        - otherwise (unknown) → `linetypeToDashConfig` → `CONTINUOUS_DASH`.
 *
 * # Deferrals (documented for the PR body)
 *
 *   - **Multi-element shader** (variant A, next PR): CENTER / DASHDOT etc. are
 *     summarised to a single dash/gap pair here — same fidelity the built-in
 *     registry already ships. The true multi-segment shader is the follow-up.
 *   - **LTSCALE / MSLTSCALE / PSLTSCALE / per-entity CELTSCALE**: NOT applied
 *     here. `computeEffectiveDash` (in `linetypePatterns.ts`) implements the
 *     math but stays UNWIRED in this PR — bucketing by CELTSCALE would multiply
 *     the bucket count (RED ZONE) and is deferred to the shader PR together
 *     with the LTSCALE sysvar plumbing.
 *   - **ByBlock → block linetype** is resolved at flatten time
 *     (`blockInsert.ts` substitutes the parent INSERT's resolved linetype for a
 *     ByBlock child before the name reaches this resolver — mirroring the
 *     ByBlock COLOR chain). By the time a name reaches `resolveBucketLinetypeDash`
 *     a `ByBlock` sentinel therefore means "no enclosing block" and resolves to
 *     Continuous, the same safe fallback the ByBlock COLOR path uses at root.
 *
 * # FIX-1 (Codex P2) — ByLayer resolves through the layer table
 *
 * `buildRenderableShell` defaults a missing/empty entity linetype to the
 * `ByLayer` sentinel (AutoCAD CELTYPE default), so MOST real entities carry
 * `ByLayer`. Returning `CONTINUOUS_DASH` for `ByLayer` BEFORE consulting the
 * layer's own linetype rendered every layer-controlled dashed/custom linetype
 * SOLID — defeating LT3-B for the common case. `resolveBucketLinetypeDash` now
 * takes the bucket's LAYER name + a `layerLinetypes` map (lower-cased layer
 * name → linetype name, built once per pass from
 * `document.styleTables.layers[].linetype`). On a `ByLayer` bucket it looks up
 * the layer's assigned linetype and RECURSES into the standard-registry /
 * custom-compile path for THAT name. A layer whose linetype is itself
 * Continuous / ByLayer / absent → `CONTINUOUS_DASH` (solid).
 */

import type { Linetype } from "@dxf-ai-editor/drawing-model";
import { signedLengthsFromLinetype } from "@dxf-ai-editor/drawing-model";

import {
  CONTINUOUS_DASH,
  compileCustomLtype,
  linetypeToDashConfig,
  needsFullPattern,
  signedToDashPattern,
  standardLinetypeFullPattern,
  LINETYPE_DASH_REGISTRY,
  type DashConfig,
  type DashPattern,
} from "./linetypePatterns";

/**
 * FIX-3 (Codex P2) — is `name` a known STANDARD linetype?
 *
 * `LINETYPE_DASH_REGISTRY` keys the mm-calibrated web registry by
 * lower-cased standard name (DASHED / CENTER / HIDDEN / ACAD_ISO* / …).
 * A name present here is a standard linetype and MUST resolve through
 * the mm registry — NOT be compiled from its `elements[]` — regardless
 * of whether `importLinetypes` stamped it `source:"seed"` or
 * `source:"file"` (a redefining DXF stamps the standard name `"file"`
 * with acad.lin INCH magnitudes, which would render ~25.4× too dense if
 * compiled directly).
 */
function isStandardLinetypeName(name: string): boolean {
  return LINETYPE_DASH_REGISTRY.has(name.trim().toLowerCase());
}

/**
 * A case-insensitive resolution map name → `Linetype`. Built once per
 * bulk-load pass from `{ ...coreSeed, ...documentLinetypes }`. Keys are
 * lower-cased; lookups normalise on read.
 */
export type LinetypeResolutionMap = ReadonlyMap<string, Linetype>;

/**
 * FIX-1 — lower-cased layer name → its assigned linetype NAME. Built once per
 * bulk-load pass from `document.styleTables.layers[].linetype`. A `ByLayer`
 * bucket resolves its dash pattern through THIS map (the layer's linetype),
 * not directly to solid.
 */
export type LayerLinetypeMap = ReadonlyMap<string, string>;

/**
 * FIX-1 — build the `LayerLinetypeMap` from the document's layer table. Keys
 * are lower-cased layer names; values are the raw `linetype` NAME each layer
 * carries (`LayerDefinition.linetype`, default `"Continuous"`). Layers whose
 * linetype is a sentinel are still recorded — the resolver treats a sentinel
 * layer-linetype as solid.
 */
export function buildLayerLinetypeMap(
  layers: Iterable<{ name: string; linetype?: string }> | undefined,
): LayerLinetypeMap {
  const map = new Map<string, string>();
  if (layers) {
    for (const layer of layers) {
      if (!layer.name) continue;
      map.set(layer.name.toLowerCase(), (layer.linetype ?? "Continuous").trim());
    }
  }
  return map;
}

/** Sentinel names that always render as a solid (continuous) line. */
function isSentinelLinetype(key: string): boolean {
  return (
    key === "" ||
    key === "continuous" ||
    key === "bylayer" ||
    key === "byblock"
  );
}

/** The `ByLayer` sentinel specifically — resolved through the layer table. */
function isByLayerSentinel(key: string): boolean {
  return key === "bylayer";
}

/**
 * Resolve a `ByLayer` linetype to the concrete linetype NAME assigned to
 * `layer` in the per-pass layer→linetype table. Returns `"Continuous"` when the
 * table / layer is absent (mirroring `resolveBucketLinetypeDash`'s ByLayer
 * redirect fallback). Used by callers that must BAKE the ByLayer sentinel into a
 * concrete name BEFORE the entity's `layer` field is rewritten (e.g. an MLEADER
 * block-content child re-homed onto the parent layer — `multileaderGeometry.ts`),
 * where the deferred bucket-time redirect would otherwise resolve against the
 * WRONG layer.
 */
export function resolveByLayerLinetypeName(
  layer: string,
  layerLinetypes?: LayerLinetypeMap,
): string {
  const layerLt = layerLinetypes?.get(layer.trim().toLowerCase());
  return layerLt && layerLt.length > 0 ? layerLt : "Continuous";
}

/**
 * Build the per-name `Linetype` resolution map for a bulk-load pass.
 *
 * # FIX-1 (Codex P2) — UNIT MISMATCH: core seed is INCHES, renderer is MM
 *
 * The `drawing-model` core `STANDARD_LINETYPES` carries the classic acad.lin
 * magnitudes in INCHES (DASHED = `0.5 / 0.25`). The renderer's web registry
 * (`LINETYPE_DASH_REGISTRY`, built from `standardLinetypes.ts`) is calibrated
 * in MILLIMETRES (Dashed = `12.5 / 6.25`). Compiling a seeded/standard name
 * through the core path therefore rendered it ~25.4× DENSER than the rest of
 * the scene.
 *
 * FIX-3 refinement: route by NAME, not by `source`. `importLinetypes` stamps a
 * STANDARD name `source:"file"` (with INCH magnitudes) whenever the imported
 * DXF redefines it, so a `source === "seed"` filter still let standard names
 * through and compiled them ~25.4× too dense. The map now carries ONLY
 * NON-STANDARD custom entries — a name NOT present in `LINETYPE_DASH_REGISTRY`.
 * Every standard name (seed OR file source) is excluded and falls through to
 * the mm-calibrated web registry in `resolveBucketLinetypeDash`, rendering
 * standard names at the existing (correct) scale with NO inch↔mm guesswork. A
 * genuine custom LTYPE (e.g. `MYDASH`) lives in the drawing's OWN units, so
 * compiling its `elements[]` directly is correct.
 *
 * `coreSeed` is intentionally IGNORED for the map contents (standard names
 * render via the web registry); the parameter is retained for call-site
 * compatibility and documents the unit-source decision.
 *
 * @param _coreSeed        core `STANDARD_LINETYPES` (inch units) — NOT mapped;
 *                         standard names resolve via the mm web registry.
 * @param documentLinetypes the drawing document's `linetypes` table (seed+file
 *                          merged). Only NON-STANDARD-name entries are kept.
 */
export function buildLinetypeResolutionMap(
  // FIX-1 — intentionally unused: seed names route through the mm web
  // registry, not the inch core seed. Retained for call-site compatibility
  // and to document the unit-source decision (underscore = unused-by-design).
  _coreSeed: Record<string, Linetype>,
  documentLinetypes: Record<string, Linetype> | undefined,
): LinetypeResolutionMap {
  const map = new Map<string, Linetype>();
  if (documentLinetypes) {
    for (const [name, lt] of Object.entries(documentLinetypes)) {
      // FIX-3 (Codex P2) — route by NAME, not by `source`. A standard
      // linetype name (DASHED / CENTER / HIDDEN / ACAD_ISO* / …) is
      // EXCLUDED from the map so it resolves through the mm-calibrated web
      // registry, REGARDLESS of source. `importLinetypes` stamps a
      // standard name `source:"file"` (not "seed") whenever the imported
      // DXF redefines/overrides it — and that record carries acad.lin INCH
      // magnitudes (DASHED 0.5/0.25). The iter-1 `source === "seed"` filter
      // let those through and compiled them directly → ~25.4× too dense.
      // Only a name NOT in the standard registry (a genuine custom LTYPE
      // like MYDASH, in the drawing's own units) is kept for direct
      // `elements[]` compilation.
      if (isStandardLinetypeName(name)) continue;
      map.set(name.toLowerCase(), lt);
    }
  }
  return map;
}

/**
 * Resolve a CONCRETE (non-ByLayer) linetype NAME into a `DashConfig`.
 *
 * Shared by the direct path and the FIX-1 ByLayer redirect (the layer's own
 * linetype is resolved through this same routing):
 *   1. sentinel (`Continuous` / `ByBlock` / empty / a residual `ByLayer`)
 *      → continuous.
 *   2. STANDARD name (in `LINETYPE_DASH_REGISTRY`) → the mm-calibrated built-in
 *      registry (`linetypeToDashConfig`), REGARDLESS of source.
 *   3. name present in the resolution map (a genuine NON-STANDARD custom entry,
 *      in the drawing's own units) → compile its `elements[]` directly.
 *   4. otherwise (unknown name) → `linetypeToDashConfig` → `CONTINUOUS_DASH`.
 */
function resolveConcreteLinetypeDash(
  name: string,
  resolution: LinetypeResolutionMap,
): DashConfig {
  const key = name.trim().toLowerCase();
  // A sentinel here (including a layer whose linetype is itself ByLayer /
  // Continuous / absent) renders solid — no further redirect.
  if (isSentinelLinetype(key)) return CONTINUOUS_DASH;

  // FIX-3 (Codex P2) — a STANDARD linetype name ALWAYS resolves through the
  // mm-calibrated web registry, regardless of source. This guard is
  // authoritative: even if a standard name somehow reached the resolution map
  // (e.g. a direct caller built it without the membership filter), we never
  // compile its INCH `elements[]` directly. `buildLinetypeResolutionMap`
  // already excludes standard names, so this is belt-and-suspenders for the
  // common path and the single source of truth for direct callers.
  if (isStandardLinetypeName(key)) return linetypeToDashConfig(name);

  // The map now holds ONLY non-standard custom LTYPEs (drawing units), so a
  // hit here is safe to compile directly.
  const lt = resolution.get(key);
  if (lt) {
    if (lt.elements.length === 0) return CONTINUOUS_DASH;
    const signed = signedLengthsFromLinetype(lt);
    return compileCustomLtype(signed);
  }

  // Non-standard name with no file entry → fall back to the built-in summary
  // registry (returns CONTINUOUS_DASH for genuinely unknown names).
  return linetypeToDashConfig(name);
}

/**
 * Resolve a bucket's linetype NAME into a `DashConfig` for the line material.
 *
 * Resolution order:
 *   1. `ByLayer` sentinel (FIX-1) → look up the bucket's LAYER in
 *      `layerLinetypes`; resolve the layer's assigned linetype NAME through
 *      `resolveConcreteLinetypeDash`. A layer whose linetype is itself
 *      Continuous / ByLayer / absent → `CONTINUOUS_DASH` (solid). No layer
 *      name / no map / layer absent → solid (the safe pre-FIX fallback).
 *   2. `Continuous` / `ByBlock` / empty sentinel → continuous. (ByBlock is
 *      already substituted with the parent INSERT linetype at flatten time —
 *      see `blockInsert.ts` — so a residual `ByBlock` here means root scope.)
 *   3. a concrete name → `resolveConcreteLinetypeDash` (standard registry /
 *      custom compile / unknown fallback).
 */
export function resolveBucketLinetypeDash(
  name: string,
  resolution: LinetypeResolutionMap,
  layer?: string,
  layerLinetypes?: LayerLinetypeMap,
): DashConfig {
  const key = name.trim().toLowerCase();

  // FIX-1 — ByLayer resolves through the layer table. The vast majority of
  // real entities carry ByLayer (CELTYPE default), so a layer-controlled
  // dashed/custom linetype MUST be honoured here or it renders solid.
  if (isByLayerSentinel(key)) {
    if (layer && layerLinetypes) {
      const layerLt = layerLinetypes.get(layer.trim().toLowerCase());
      if (layerLt) return resolveConcreteLinetypeDash(layerLt, resolution);
    }
    return CONTINUOUS_DASH;
  }

  // Continuous / ByBlock / empty → solid.
  if (isSentinelLinetype(key)) return CONTINUOUS_DASH;

  return resolveConcreteLinetypeDash(name, resolution);
}

/**
 * PR-LT3 variant A (A1) — resolve a CONCRETE (non-ByLayer) linetype NAME into a
 * FULL {@link DashPattern}, or `null` when the single-pair `DashConfig` fast
 * path suffices.
 *
 * Returns a multi-element pattern ONLY when the linetype is LOSSILY summarised
 * by the single-pair path (`> 2` elements OR a dot — see `needsFullPattern`).
 * A `null` return means the caller keeps the existing `resolveConcreteLinetypeDash`
 * single-pair result (byte-identical for faithful 2-element dash/gap linetypes):
 *   1. sentinel (`Continuous` / `ByBlock` / empty / residual `ByLayer`) → `null`
 *      (solid; no pattern).
 *   2. STANDARD name → the mm-calibrated full pattern from the on-screen
 *      registry (`standardLinetypeFullPattern`), or `null` for a faithful
 *      2-element standard name (DASHED / HIDDEN).
 *   3. NON-STANDARD custom entry (in the map, drawing units) → its signed
 *      `elements[]` as a full pattern WHEN `needsFullPattern`, else `null`.
 *   4. unknown name → `null`.
 */
function resolveConcreteLinetypePattern(
  name: string,
  resolution: LinetypeResolutionMap,
): DashPattern | null {
  const key = name.trim().toLowerCase();
  if (isSentinelLinetype(key)) return null;

  // STANDARD name → mm-calibrated full pattern (matches the single-pair
  // registry's units). `null` for a faithful 2-element standard linetype.
  if (isStandardLinetypeName(key)) return standardLinetypeFullPattern(name);

  // NON-STANDARD custom LTYPE (drawing units) — full pattern only when lossy.
  const lt = resolution.get(key);
  if (lt) {
    if (lt.elements.length === 0) return null;
    const signed = signedLengthsFromLinetype(lt);
    if (!needsFullPattern(signed)) return null;
    return signedToDashPattern(signed);
  }

  // Unknown name → no full pattern (single-pair path resolves it to solid).
  return null;
}

/**
 * A1 — resolve a bucket's linetype NAME into a FULL {@link DashPattern} for the
 * multi-element shader texture path, or `null` when the single-pair
 * `DashConfig` (from `resolveBucketLinetypeDash`) suffices.
 *
 * Mirrors `resolveBucketLinetypeDash`'s routing exactly (ByLayer redirect →
 * sentinel → concrete) so the two resolvers agree on the SAME effective
 * linetype name for a bucket; only the return SHAPE differs (full pattern vs
 * single pair). A `null` here ⇒ the caller uses the single-pair path.
 */
export function resolveBucketLinetypePattern(
  name: string,
  resolution: LinetypeResolutionMap,
  layer?: string,
  layerLinetypes?: LayerLinetypeMap,
): DashPattern | null {
  const key = name.trim().toLowerCase();

  // ByLayer → resolve through the layer's assigned linetype (mirrors the
  // single-pair path's FIX-1 redirect).
  if (isByLayerSentinel(key)) {
    if (layer && layerLinetypes) {
      const layerLt = layerLinetypes.get(layer.trim().toLowerCase());
      if (layerLt) return resolveConcreteLinetypePattern(layerLt, resolution);
    }
    return null;
  }

  if (isSentinelLinetype(key)) return null;

  return resolveConcreteLinetypePattern(name, resolution);
}
